/**
 * Centralized React Query hooks for the hot-path API endpoints.
 *
 * Usage:
 *   const { data: me, isLoading } = useMe();
 *   const { data: challenges } = usePublicChallenges();
 *   const { data: reminders } = useReminders();
 *
 * Benefits over raw fetch + useEffect:
 * - Automatic dedup: 5 components calling useMe() = 1 HTTP request
 * - Automatic cache with TTL (staleTime from queryClient, 30s)
 * - Automatic refetch on remount (when stale)
 * - Shared state: one mutation can invalidate all queries
 */
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { httpClient } from './http';
import { API_URL } from '../config/api';

// ═══════════════════════════════════════════════════════════════
// Query keys — keep them stable and parameterized
// ═══════════════════════════════════════════════════════════════
export const qk = {
  me: ['me'],
  reminders: ['reminders'],
  publicChallenges: ['challenges', 'public'],
  myChallenges: ['challenges', 'mine'],
  notifications: (skip = 0, limit = 20) => ['notifications', { skip, limit }],
  posts: (skip = 0, limit = 10) => ['posts', { skip, limit }],
  feedFollowing: (skip = 0, limit = 10) => ['feed', 'following', { skip, limit }],
  userProfile: (userId) => ['user', userId],
};

// ═══════════════════════════════════════════════════════════════
// Current user
// ═══════════════════════════════════════════════════════════════
export function useMe(options = {}) {
  return useQuery({
    queryKey: qk.me,
    queryFn: async () => {
      const res = await httpClient.get(`${API_URL}/api/auth/me`);
      return res.data;
    },
    staleTime: 60_000, // user data rarely changes mid-session
    enabled: options.enabled !== false,
    ...options,
  });
}

// ═══════════════════════════════════════════════════════════════
// Reminders (per-user + templates)
// ═══════════════════════════════════════════════════════════════
export function useReminders() {
  return useQuery({
    queryKey: qk.reminders,
    queryFn: async () => {
      const res = await httpClient.get(`${API_URL}/api/reminders`);
      return res.data.reminders || [];
    },
    staleTime: 30_000,
  });
}

export function useUpdateReminder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ reminderId, enabled, time, timezone }) => {
      const res = await httpClient.put(
        `${API_URL}/api/reminders/${reminderId}`,
        { enabled, time, timezone }
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.reminders });
    },
  });
}

// ═══════════════════════════════════════════════════════════════
// Challenges
// ═══════════════════════════════════════════════════════════════
export function usePublicChallenges() {
  return useQuery({
    queryKey: qk.publicChallenges,
    queryFn: async () => {
      const res = await httpClient.get(`${API_URL}/api/challenges/public`);
      return res.data || [];
    },
    staleTime: 60_000, // backend already caches 60s — align
  });
}

export function useMyChallenges() {
  return useQuery({
    queryKey: qk.myChallenges,
    queryFn: async () => {
      // Backend exposes the user's challenges at /api/challenges (see routers/challenges.py).
      const res = await httpClient.get(`${API_URL}/api/challenges`);
      return res.data || [];
    },
    staleTime: 30_000,
  });
}

// Invalidate all challenge queries after a mutation (create/join/leave/delete)
export function useInvalidateChallenges() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['challenges'] });
  };
}

// ═══════════════════════════════════════════════════════════════
// Premium status — hot path, called from Dashboard, Profilo,
// MealPlan, Premium page (4 independent components).
// Without dedup: 4 HTTP requests on a single page load.
// With dedup: 1 HTTP request shared across all consumers.
// ═══════════════════════════════════════════════════════════════
export function usePremiumStatus(options = {}) {
  return useQuery({
    queryKey: ['premium', 'status'],
    queryFn: async () => {
      const res = await httpClient.get(`${API_URL}/api/premium/status`);
      return res.data || {};
    },
    // Premium flags change rarely within a session (only after purchase/trial start)
    staleTime: 60_000,
    enabled: options.enabled !== false,
  });
}

export function useInvalidatePremiumStatus() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['premium', 'status'] });
  };
}

// ═══════════════════════════════════════════════════════════════
// Dashboard-only endpoints (buttons config, home ad banner)
// ═══════════════════════════════════════════════════════════════
export function useDashboardButtons(options = {}) {
  return useQuery({
    queryKey: ['dashboard-buttons'],
    queryFn: async () => {
      const res = await httpClient.get(`${API_URL}/api/dashboard-buttons`);
      return res.data?.buttons || null;
    },
    staleTime: 5 * 60_000, // admin-configured, changes very rarely
    enabled: options.enabled !== false,
  });
}

export function useAdBanner(position, options = {}) {
  return useQuery({
    queryKey: ['ad-banner', position],
    queryFn: async () => {
      const res = await httpClient.get(`${API_URL}/api/ads/banner/${position}`);
      return res.data || null;
    },
    staleTime: 5 * 60_000,
    enabled: options.enabled !== false && !!position,
  });
}

// ═══════════════════════════════════════════════════════════════
// Live walkers count — polled every 10s on the Camminata page.
// Using React Query's refetchInterval lets the browser pause polling
// when the tab is backgrounded (much friendlier on mobile battery
// than a raw setInterval).
// ═══════════════════════════════════════════════════════════════
export function useLiveWalkersCount(options = {}) {
  return useQuery({
    queryKey: ['walks', 'live', 'count'],
    queryFn: async () => {
      const res = await httpClient.get(`${API_URL}/api/walks/live/count`);
      return res.data || { count: 0, walkers: [] };
    },
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
    staleTime: 5_000,
    enabled: options.enabled !== false,
  });
}

// ═══════════════════════════════════════════════════════════════
// Notifications (inbox bell)
// ═══════════════════════════════════════════════════════════════
export function useUnreadCount(options = {}) {
  return useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      try {
        const res = await httpClient.get(`${API_URL}/api/notifications/unread-count`);
        return res.data || { count: 0 };
      } catch {
        return { count: 0 };
      }
    },
    refetchInterval: 30_000, // poll every 30s — much cheaper than re-rendering
    staleTime: 15_000,
    enabled: options.enabled !== false,
  });
}

export function useNotifications(options = {}) {
  return useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: async () => {
      try {
        const res = await httpClient.get(`${API_URL}/api/notifications`);
        const data = res.data || {};
        // Backend returns { notifications: [...], unread_count: N }
        return Array.isArray(data) ? data : (data.notifications || []);
      } catch {
        return [];
      }
    },
    staleTime: 15_000,
    enabled: options.enabled !== false,
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await httpClient.put(`${API_URL}/api/notifications/read-all`, {});
    },
    onSuccess: () => {
      qc.setQueryData(['notifications', 'unread-count'], { count: 0 });
      qc.setQueryData(['notifications', 'list'], (old) => (old ? old.map((n) => ({ ...n, read: true })) : old));
    },
  });
}

// ═══════════════════════════════════════════════════════════════
// Infinite scrolling feed posts.
//
// Backend (`GET /api/posts?page=N&limit=10&filter=all|challenges`) returns:
//   { posts: [...], has_more: bool, page: int }
//
// React Query's useInfiniteQuery handles page accumulation, dedup and
// background refetch automatically. We keep one query key per filter so
// switching tabs is instant (the previously-fetched pages stay in cache).
// ═══════════════════════════════════════════════════════════════
export function useInfinitePosts(filter = 'all', options = {}) {
  return useInfiniteQuery({
    queryKey: ['posts', 'feed', filter],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await httpClient.get(
        `${API_URL}/api/posts?page=${pageParam}&limit=10&filter=${filter}`
      );
      const data = res.data || {};
      return {
        posts: data.posts || [],
        has_more: !!data.has_more,
        page: data.page || pageParam,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.has_more ? (lastPage.page || 1) + 1 : undefined),
    staleTime: 30_000,
    // Refetch when the user comes back to the tab (network may have stale posts).
    refetchOnWindowFocus: true,
    enabled: options.enabled !== false,
  });
}

/** Flatten all pages of an `useInfinitePosts` result into a single array. */
export function flattenInfinitePosts(infiniteData) {
  if (!infiniteData?.pages) return [];
  return infiniteData.pages.flatMap((p) => p.posts || []);
}

/**
 * Update a single post across every page of every `useInfinitePosts` cache.
 * Used by optimistic mutations (reactions, comments, reports).
 */
export function updateInfinitePostInCache(qc, postId, updater) {
  qc.getQueryCache()
    .findAll({ queryKey: ['posts', 'feed'] })
    .forEach((q) => {
      qc.setQueryData(q.queryKey, (old) => {
        if (!old?.pages) return old;
        let changed = false;
        const newPages = old.pages.map((page) => {
          const newPosts = page.posts.map((p) => {
            if (p.post_id === postId) { changed = true; return updater(p); }
            return p;
          });
          return changed ? { ...page, posts: newPosts } : page;
        });
        return changed ? { ...old, pages: newPages } : old;
      });
    });
}



// ═══════════════════════════════════════════════════════════════
// PRIVATE MESSAGING
// ═══════════════════════════════════════════════════════════════
export function useMessagesUnread(options = {}) {
  return useQuery({
    queryKey: ['messages', 'unread-count'],
    queryFn: async () => {
      try {
        const res = await httpClient.get(`${API_URL}/api/messages/unread-count`);
        return res.data || { unread: 0, pending_requests: 0 };
      } catch {
        return { unread: 0, pending_requests: 0 };
      }
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
    enabled: options.enabled !== false,
  });
}

export function useConversations(folder = 'inbox', options = {}) {
  return useQuery({
    queryKey: ['messages', 'conversations', folder],
    queryFn: async () => {
      const res = await httpClient.get(
        `${API_URL}/api/messages/conversations?folder=${folder}`
      );
      return res.data?.conversations || [];
    },
    // Poll the open inbox tab every 10s for near-real-time updates.
    refetchInterval: options.refetchInterval ?? 10_000,
    staleTime: 5_000,
    enabled: options.enabled !== false,
  });
}

export function useConversation(conversationId, options = {}) {
  return useQuery({
    queryKey: ['messages', 'conversation', conversationId],
    queryFn: async () => {
      const res = await httpClient.get(
        `${API_URL}/api/messages/conversations/${conversationId}`
      );
      return res.data || { conversation: null, messages: [], has_more: false };
    },
    // Faster polling while the chat screen is open.
    refetchInterval: options.refetchInterval ?? 4_000,
    staleTime: 1_000,
    enabled: !!conversationId && options.enabled !== false,
  });
}

export function useCanMessage(userId, options = {}) {
  return useQuery({
    queryKey: ['messages', 'can-message', userId],
    queryFn: async () => {
      const res = await httpClient.get(`${API_URL}/api/messages/can-message/${userId}`);
      return res.data;
    },
    staleTime: 60_000,
    enabled: !!userId && options.enabled !== false,
  });
}

// ═══════════════════════════════════════════════════════════════
// Today's Health activity (steps → km, calories, streak)
// Backed by GET /api/health/today (single indexed Mongo lookup).
// ═══════════════════════════════════════════════════════════════
export function useTodayActivity(options = {}) {
  return useQuery({
    queryKey: ['health', 'today'],
    queryFn: async () => {
      const res = await httpClient.get(`${API_URL}/api/health/today`);
      return res.data;
    },
    staleTime: 5 * 60_000, // Health sync runs periodically; 5 min is plenty
    enabled: options.enabled !== false,
    ...options,
  });
}

